import { useState, useCallback, useEffect, useRef } from 'react';
import { ExternalLink, RefreshCw } from 'lucide-react';
import {
  isDevboxStatusPollEligible,
  isResumableDevboxStatus,
  useResumeDevbox,
} from '@reflex/ui/client/hooks';
import { Spinner } from '@reflex/ui/components/shared/Spinner';
import { PageDescription } from '@reflex/ui/components/layout/Typography';
import { Button } from '@reflex/ui/components/ui/button';
import { useTunnelUrl } from '@/hooks/config/useConfig';
import { useDevboxStatus } from '@/hooks/agents/useDevboxStatus';

interface AgentPreviewPanelProps {
  agentId: string;
  /** Live agent lifecycle status, used to skip the resume poll for dead boxes. */
  agentStatus: string;
  /** The agent's devbox id, if one has been allocated. */
  devboxId: string | null | undefined;
  tunnelKey: string | null | undefined;
  port: number;
}

export function AgentPreviewPanel({
  agentId,
  agentStatus,
  devboxId,
  tunnelKey,
  port,
}: AgentPreviewPanelProps) {
  const url = useTunnelUrl(tunnelKey, port);
  const [loading, setLoading] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Opening the preview points the iframe at the dev-server tunnel. A suspended
  // box would eventually wake from that HTTP probe, but the probe doesn't prime
  // the status cache and can lag, leaving a blank iframe. So when the box is in
  // a resumable state, explicitly resume it (the server no-ops if it isn't
  // suspended) so the dev server comes back up promptly. The one-shot guard
  // keeps a later re-suspend from re-triggering while this tab stays open.
  const pollEligible = isDevboxStatusPollEligible(agentStatus, devboxId);
  const { data: devboxStatus } = useDevboxStatus(agentId, { enabled: pollEligible });
  const { mutate: resumeDevbox } = useResumeDevbox(agentId);
  const resumeAttempted = useRef(false);
  // Only trust the status while the poll is eligible. Once the agent is gone,
  // TanStack keeps the last polled status cached (a box last seen `suspended`
  // lingers), so gating on `pollEligible` avoids firing a resume at a dead box.
  const status = pollEligible ? devboxStatus?.status : undefined;
  useEffect(() => {
    if (resumeAttempted.current) return;
    if (isResumableDevboxStatus(status)) {
      resumeAttempted.current = true;
      resumeDevbox();
    }
  }, [status, resumeDevbox]);

  const handleLoad = useCallback(() => {
    setLoading(false);
  }, []);

  const handleRefresh = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;
    setLoading(true);
    iframe.contentWindow.location.reload();
  }, []);

  if (!url) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <PageDescription className="mt-0 text-sm">Waiting for tunnel connection...</PageDescription>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
        <span className="truncate text-xs text-muted-foreground font-mono">:{port}</span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={handleRefresh}
            aria-label="Refresh preview"
          >
            <RefreshCw className="h-3 w-3" />
          </Button>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            aria-label="Open in new tab"
          >
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>
      <div className="relative flex-1">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80">
            <Spinner size="md" className="text-muted-foreground" />
          </div>
        )}
        <iframe
          ref={iframeRef}
          src={url}
          onLoad={handleLoad}
          className="h-full w-full border-0"
          title="Dev server preview"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        />
      </div>
    </div>
  );
}
